'use strict';

var richTextTypes = require('@contentful/rich-text-types');
var _ = require('lodash');
var gfm = require('remark-gfm');
var markdown = require('remark-parse');
var unified = require('unified');

const markdownNodeTypes = new Map([
    [
        'paragraph',
        richTextTypes.BLOCKS.PARAGRAPH
    ],
    [
        'heading',
        'heading'
    ],
    [
        'text',
        'text'
    ],
    [
        'emphasis',
        'text'
    ],
    [
        'strong',
        'text'
    ],
    [
        'delete',
        'text'
    ],
    [
        'inlineCode',
        'text'
    ],
    [
        'link',
        richTextTypes.INLINES.HYPERLINK
    ],
    [
        'thematicBreak',
        richTextTypes.BLOCKS.HR
    ],
    [
        'blockquote',
        richTextTypes.BLOCKS.QUOTE
    ],
    [
        'list',
        'list'
    ],
    [
        'listItem',
        richTextTypes.BLOCKS.LIST_ITEM
    ],
    [
        'table',
        richTextTypes.BLOCKS.TABLE
    ],
    [
        'tableRow',
        richTextTypes.BLOCKS.TABLE_ROW
    ],
    [
        'tableCell',
        richTextTypes.BLOCKS.TABLE_CELL
    ]
]);
const nodeTypeFor = (node)=>{
    const nodeType = markdownNodeTypes.get(node.type);
    switch(nodeType){
        case 'heading':
            return `${nodeType}-${node.depth}`;
        case 'list':
            return `${node.ordered ? 'ordered' : 'unordered'}-list`;
        default:
            return nodeType;
    }
};
const markTypes = new Map([
    [
        'emphasis',
        'italic'
    ],
    [
        'strong',
        'bold'
    ],
    [
        'inlineCode',
        'code'
    ],
    [
        'delete',
        'strikethrough'
    ]
]);
const markTypeFor = (node)=>{
    return markTypes.get(node.type);
};
const isLink = (node)=>{
    return node.type === 'link';
};
const nodeContainerTypes = new Map([
    [
        'delete',
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_1,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_2,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_3,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_4,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_5,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HEADING_6,
        'block'
    ],
    [
        richTextTypes.BLOCKS.LIST_ITEM,
        'block'
    ],
    [
        richTextTypes.BLOCKS.UL_LIST,
        'block'
    ],
    [
        richTextTypes.BLOCKS.OL_LIST,
        'block'
    ],
    [
        richTextTypes.BLOCKS.QUOTE,
        'block'
    ],
    [
        richTextTypes.BLOCKS.HR,
        'block'
    ],
    [
        richTextTypes.BLOCKS.PARAGRAPH,
        'block'
    ],
    [
        richTextTypes.BLOCKS.TABLE,
        'block'
    ],
    [
        richTextTypes.BLOCKS.TABLE_CELL,
        'block'
    ],
    [
        richTextTypes.BLOCKS.TABLE_HEADER_CELL,
        'block'
    ],
    [
        richTextTypes.BLOCKS.TABLE_ROW,
        'block'
    ],
    [
        richTextTypes.INLINES.HYPERLINK,
        'inline'
    ],
    [
        'text',
        'text'
    ],
    [
        'emphasis',
        'text'
    ],
    [
        'strong',
        'text'
    ],
    [
        'inlineCode',
        'text'
    ],
    [
        'delete',
        'text'
    ]
]);
const isBlock = (nodeType)=>{
    return nodeContainerTypes.get(nodeType) === 'block';
};
const isText = (nodeType)=>{
    return nodeContainerTypes.get(nodeType) === 'text';
};
const isInline = (nodeType)=>{
    return nodeContainerTypes.get(nodeType) === 'inline';
};
const isTableCell = (nodeType)=>{
    return nodeType === richTextTypes.BLOCKS.TABLE_CELL;
};
const buildHyperlink = async (node, fallback, appliedMarksTypes)=>{
    const content = await mdToRichTextNodes(node.children, fallback, appliedMarksTypes);
    const hyperlink = {
        nodeType: richTextTypes.INLINES.HYPERLINK,
        data: {
            uri: node.url
        },
        content
    };
    return [
        hyperlink
    ];
};
const buildGenericBlockOrInline = async (node, fallback, appliedMarksTypes)=>{
    const nodeType = nodeTypeFor(node);
    const content = await mdToRichTextNodes(node.children, fallback, appliedMarksTypes);
    return [
        {
            nodeType: nodeType,
            content,
            data: {}
        }
    ];
};
const buildTableCell = async (node, fallback, appliedMarksTypes)=>{
    const nodeChildren = await mdToRichTextNodes(node.children, fallback, appliedMarksTypes);
    const content = nodeChildren.reduce((result, contentNode)=>{
        if (isText(contentNode.nodeType) || isInline(contentNode.nodeType)) {
            const lastNode = result[result.length - 1];
            if (lastNode && lastNode.nodeType === richTextTypes.BLOCKS.PARAGRAPH) {
                lastNode.content.push(contentNode);
            } else {
                result.push({
                    nodeType: richTextTypes.BLOCKS.PARAGRAPH,
                    data: {},
                    content: [
                        contentNode
                    ]
                });
            }
        } else {
            result.push(contentNode);
        }
        return result;
    }, []);
    if (content.length === 0) {
        content.push({
            nodeType: richTextTypes.BLOCKS.PARAGRAPH,
            data: {},
            content: [
                {
                    nodeType: 'text',
                    data: {},
                    marks: [],
                    value: ''
                }
            ]
        });
    }
    return [
        {
            nodeType: richTextTypes.BLOCKS.TABLE_CELL,
            content,
            data: {}
        }
    ];
};
const buildText = async (node, fallback, appliedMarksTypes)=>{
    const nodeType = nodeTypeFor(node);
    const markType = markTypeFor(node);
    const marks = Array.from(appliedMarksTypes);
    if (markType) {
        marks.push(markType);
    }
    if (node.type !== 'text' && node.children) {
        return await mdToRichTextNodes(node.children, fallback, marks);
    }
    if (node.value) {
        return [
            {
                nodeType: nodeType,
                value: node.value,
                marks: marks.map((type)=>({
                        type
                    })),
                data: {}
            }
        ];
    }
};
const buildFallbackNode = async (node, fallback)=>{
    const fallbackResult = await fallback(node);
    if (_.isArray(fallbackResult)) {
        return fallbackResult;
    }
    return [
        fallbackResult
    ];
};
async function mdToRichTextNode(node, fallback, appliedMarksTypes = []) {
    if (node.type === 'html' && /<br\s?\/?>/gi.test(node.value)) {
        node.value = '\n';
        node.type = 'text';
    }
    const nodeType = nodeTypeFor(node);
    if (isLink(node)) {
        return await buildHyperlink(node, fallback, appliedMarksTypes);
    }
    if (isTableCell(nodeType)) {
        return await buildTableCell(node, fallback, appliedMarksTypes);
    }
    if (isBlock(nodeType) || isInline(nodeType)) {
        return await buildGenericBlockOrInline(node, fallback, appliedMarksTypes);
    }
    if (isText(nodeType)) {
        return await buildText(node, fallback, appliedMarksTypes);
    }
    return await buildFallbackNode(node, fallback);
}
async function mdToRichTextNodes(nodes, fallback, appliedMarksTypes = []) {
    if (!nodes) {
        return Promise.resolve([]);
    }
    const rtNodes = await Promise.all(nodes.map((node)=>mdToRichTextNode(node, fallback, appliedMarksTypes)));
    return _.flatten(rtNodes).filter(Boolean);
}
const astToRichTextDocument = async (tree, fallback)=>{
    const content = await mdToRichTextNodes(tree.children, fallback);
    return {
        nodeType: richTextTypes.BLOCKS.DOCUMENT,
        data: {},
        content: content
    };
};
function expandParagraphWithInlineImages(node) {
    if (node.type !== 'paragraph') {
        return [
            node
        ];
    }
    const imageNodeIndices = [];
    for(let i = 0; i < node.children.length; i++){
        if (node.children[i].type === 'image') {
            imageNodeIndices.push(i);
        }
    }
    if (imageNodeIndices.length === 0) {
        return [
            node
        ];
    }
    const allNodes = [];
    let lastIndex = -1;
    for(let j = 0; j < imageNodeIndices.length; j++){
        const index = imageNodeIndices[j];
        if (index !== 0) {
            const nodesBefore = node.children.slice(lastIndex + 1, index);
            if (nodesBefore.length > 0) {
                allNodes.push({
                    ...node,
                    children: nodesBefore
                });
            }
        }
        const imageNode = node.children[index];
        allNodes.push(imageNode);
        let nodesAfter = [];
        const rangeEnd = j + 1 < imageNodeIndices.length ? imageNodeIndices[j + 1] : node.children.length;
        if (index + 1 < rangeEnd && index === imageNodeIndices.slice(-1)[0]) {
            nodesAfter = node.children.slice(index + 1, rangeEnd);
            if (nodesAfter.length > 0) {
                allNodes.push({
                    ...node,
                    children: nodesAfter
                });
            }
        }
        lastIndex = index;
    }
    return allNodes;
}
function prepareMdAST(ast) {
    function prepareASTNodeChildren(node) {
        if (!node.children) {
            return node;
        }
        const children = _.flatMap(node.children, (n)=>expandParagraphWithInlineImages(n)).map((n)=>prepareASTNodeChildren(n));
        return {
            ...node,
            children
        };
    }
    return prepareASTNodeChildren({
        depth: '0',
        type: 'root',
        value: '',
        ordered: true,
        children: ast.children
    });
}
async function richTextFromMarkdown(md, fallback = ()=>Promise.resolve(null)) {
    const processor = unified().use(markdown).use(gfm);
    const tree = processor.parse(md);
    const ast = prepareMdAST(tree);
    return await astToRichTextDocument(ast, fallback);
}

exports.richTextFromMarkdown = richTextFromMarkdown;
//# sourceMappingURL=rich-text-from-markdown.es5.js.map
